import { type JSX, useState } from "react";
import styled from "styled-components";
import { currentColors } from "../../shared/colors";
import t from "../../shared/translations";

interface ILeaderboard {
    id: string;
    name: string;
    mode: string;
    specifiedTasks: {
        id: string;
    }[];
    specifiedFeedbackTasks: {
        id: string;
    }[];
    rankings: {
        pathMembershipId: string;
        firstName: string;
        lastName: string;
        rank: number;
        miles: number;
    }[];
}

interface IProps {
    leaderboard: ILeaderboard;
    currentPlayerMembershipId: string;
}

const StyledLeaderboardTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    margin-top: 0.5rem;
    border: 1px solid ${currentColors.performance};
    
    th {
        background-color: ${currentColors.performance};
        color: white;
        text-align: left;
    }
    
    tr:nth-child(even) {
        background-color: ${currentColors.performanceBackground};
    }
`;

const CurrentPlayerRow = styled.tr`
    font-weight: bold;
`;

const EllipsisRow = styled.tr`
    text-align: center;
    font-style: italic;
    color: ${currentColors.text};
`;

const ShowMoreRow = styled.tr`
    td {
        text-align: center;
    }
`;

const ShowMoreButton = styled.button`
    margin: 0;
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
    background-color: ${currentColors.button};
    color: white;
    border: none;
    cursor: pointer;
    display: inline-block;
`;

function LeaderboardTable({ leaderboard, currentPlayerMembershipId }: IProps): JSX.Element {
    const [displayLimit, setDisplayLimit] = useState(10);
    const rankings = leaderboard.rankings;
    const currentPlayerIndex = rankings.findIndex((r) => r.pathMembershipId.toString() === currentPlayerMembershipId);
    const currentPlayerRanking = currentPlayerIndex >= 0 ? rankings[currentPlayerIndex] : undefined;

    const topRankings = rankings.slice(0, displayLimit);
    let showCurrentPlayerSeparately = false;
    let showEllipsis = false;
    let showMoreButton = false;

    if (currentPlayerIndex >= 0) {
        if (currentPlayerIndex === displayLimit) {
            // Case: player is just after display limit (e.g., index 10 when displayLimit is 10)
            showCurrentPlayerSeparately = true;
            // No ellipsis needed since player is immediately after
            showMoreButton = rankings.length > displayLimit + 1; // More rows beyond current player
        } else if (currentPlayerIndex > displayLimit) {
            // Case: player is further down (e.g., index 11+ when displayLimit is 10)
            showCurrentPlayerSeparately = true;
            showEllipsis = true;
            // Show more if there are hidden rows (ellipsis) or more rows beyond current player
            showMoreButton = true; // Always true when ellipsis is shown, as there are hidden rows
        } else {
            // Case: player is within display limit (index < displayLimit)
            // No special handling needed, just check if there are more rows
            showMoreButton = rankings.length > displayLimit;
        }
    } else {
        // Case: current player not found
        showMoreButton = rankings.length > displayLimit;
    }

    const handleShowMore = () => {
        setDisplayLimit((currLimit) => (currLimit === 10 ? 25 : currLimit + 25));
    };

    return (
        <StyledLeaderboardTable>
            <thead>
                <tr>
                    <th>{t("player.performance.leaderboard-table-heading-rank")}</th>
                    <th>{t("player.performance.leaderboard-table-heading-player")}</th>
                    <th>{t("player.performance.leaderboard-table-heading-miles")}</th>
                </tr>
            </thead>
            <tbody>
                {topRankings.map((ranking) => {
                    const isCurrentPlayer = ranking.pathMembershipId.toString() === currentPlayerMembershipId;
                    const RowComponent = isCurrentPlayer ? CurrentPlayerRow : "tr";

                    return (
                        <RowComponent key={ranking.pathMembershipId}>
                            <td>{ranking.rank}</td>
                            <td>
                                {ranking.firstName} {ranking.lastName}
                            </td>
                            <td>{ranking.miles}</td>
                        </RowComponent>
                    );
                })}
                {showEllipsis && (
                    <EllipsisRow>
                        <td colSpan={3}>{t("player.performance.leaderboard-table-ellipsis")}</td>
                    </EllipsisRow>
                )}
                {showCurrentPlayerSeparately && (
                    <CurrentPlayerRow key={`current-${currentPlayerRanking!.pathMembershipId}`}>
                        <td>{currentPlayerRanking!.rank}</td>
                        <td>
                            {currentPlayerRanking!.firstName} {currentPlayerRanking!.lastName}
                        </td>
                        <td>{currentPlayerRanking!.miles}</td>
                    </CurrentPlayerRow>
                )}
                {showMoreButton && (
                    <ShowMoreRow>
                        <td colSpan={3}>
                            <ShowMoreButton onClick={handleShowMore}>
                                {t("player.performance.leaderboard-show-more")}
                            </ShowMoreButton>
                        </td>
                    </ShowMoreRow>
                )}
            </tbody>
        </StyledLeaderboardTable>
    );
}

export default LeaderboardTable;
